import { cn } from '@/lib/utils'
import {
    BoldIcon,
    CodeIcon,
    HeadingIcon,
    ImageIcon,
    ImageUpIcon,
    ItalicIcon,
    LinkIcon,
    ListIcon,
    ListOrderedIcon,
    ListTodoIcon,
} from 'lucide-react'
import type { editor as monaco } from 'monaco-editor'
import { lazy, type ReactNode, Suspense, useRef, useState } from 'react'
import IconButton from '../Button/IconButton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Tooltip } from '../ui/tooltip'
import { IconPicker } from './IconPicker'
import {
    alternarPrefixo,
    inserirNoTexto,
    tokenImagem,
    tokenLucideIcon,
} from './markdown-tokens'
import { MarkdownView } from './MarkdownView'

/** Prefixos de bloco da barra: como marcar a linha e como reconhecê-la marcada. */
const CABECALHO = { montar: () => '### ', padrao: /^#{1,6} / }
const MARCADOR = { montar: () => '- ', padrao: /^- (?!\[[ x]\] )/ }
const NUMERO = {
    montar: (ordem: number) => `${ordem + 1}. `,
    padrao: /^\d+\. /,
}
const TAREFA = { montar: () => '- [ ] ', padrao: /^- \[[ x]\] / }

/**
 * Todas as abas ocupam o espaço que sobra no editor, para a troca de aba não
 * pular o layout; `min-h-0` impede o conteúdo de esticar o flex item.
 */
const ALTURA_ABA = 'flex-1 min-h-0'

// O Monaco pesa alguns MB: só desce quando um editor é aberto.
const EditorMonaco = lazy(() =>
    import('./monaco').then(modulo => ({ default: modulo.EditorMonaco })),
)

const CARREGANDO_EDITOR = (
    <span className="p-3 text-sm text-muted-foreground">
        Carregando editor...
    </span>
)

/** Aba adicional exibida depois de "Pré-visualizar". */
interface AbaExtraMarkdown {
    valor: string
    rotulo: string
    conteudo: ReactNode
}

interface IProps {
    value: string
    onChange: (valor: string) => void
    /**
     * Guarda os arquivos e devolve o índice de cada imagem aceita, na ordem em
     * que entraram na lista — é o índice que o token do corpo referencia.
     */
    onAnexarImagens?: (arquivos: File[]) => Promise<string[]>
    /** Resolve o índice do token para exibir a imagem na pré-visualização. */
    resolverImagem?: (imagemId: string) => string | undefined
    abasExtras?: AbaExtraMarkdown[]
    /** Texto de referência: as linhas que diferem dele ganham marca no gutter. */
    textoOriginal?: string
    placeholder?: string
    /** Sem altura aqui ou no pai, o editor ocupa todo o espaço disponível. */
    className?: string
    disabled?: boolean
}

export function MarkdownEditor({
    value,
    onChange,
    onAnexarImagens,
    resolverImagem,
    abasExtras = [],
    textoOriginal,
    placeholder,
    className,
    disabled,
}: IProps) {
    const editor = useRef<monaco.IStandaloneCodeEditor | null>(null)
    const seletor = useRef<HTMLInputElement>(null)
    const [arrastando, setArrastando] = useState(false)

    function selecaoAtual() {
        const instancia = editor.current
        const modelo = instancia?.getModel()
        const selecao = instancia?.getSelection()
        if (!instancia || !modelo || !selecao) return

        return {
            instancia,
            modelo,
            inicio: modelo.getOffsetAt(selecao.getStartPosition()),
            fim: modelo.getOffsetAt(selecao.getEndPosition()),
        }
    }

    // Edita pelo próprio Monaco, e não pela prop, para a ação da barra entrar
    // no histórico de desfazer; o `onChange` do editor propaga o texto novo.
    function substituir(
        { instancia, modelo }: NonNullable<ReturnType<typeof selecaoAtual>>,
        texto: string,
        inicio: number,
        fim = inicio,
    ) {
        instancia.executeEdits('barra-markdown', [
            { range: modelo.getFullModelRange(), text: texto },
        ])
        const de = modelo.getPositionAt(inicio)
        const ate = modelo.getPositionAt(fim)
        instancia.setSelection({
            startLineNumber: de.lineNumber,
            startColumn: de.column,
            endLineNumber: ate.lineNumber,
            endColumn: ate.column,
        })
        instancia.focus()
    }

    function aplicar(abertura: string, fechamento = '') {
        const alvo = selecaoAtual()
        if (!alvo) return

        const { texto, cursor } = inserirNoTexto(
            alvo.modelo.getValue(),
            abertura,
            alvo.inicio,
            alvo.fim,
            fechamento,
        )
        substituir(alvo, texto, cursor)
    }

    function prefixar({
        montar,
        padrao,
    }: {
        montar: (ordem: number) => string
        padrao: RegExp
    }) {
        const alvo = selecaoAtual()
        if (!alvo) return

        const { texto, inicio, fim } = alternarPrefixo(
            alvo.modelo.getValue(),
            montar,
            padrao,
            alvo.inicio,
            alvo.fim,
        )
        substituir(alvo, texto, inicio, fim)
    }

    async function anexar(arquivos: File[]) {
        const alvo = selecaoAtual()
        if (!onAnexarImagens || !arquivos.length || !alvo) return

        const ids = await onAnexarImagens(arquivos)

        if (!ids.length) return

        // A leitura dos arquivos é assíncrona e o usuário pode continuar
        // digitando nesse meio tempo; o texto vem do modelo, não do início da
        // chamada, para não apagar o que ele escreveu enquanto a imagem carregava.
        const { texto, cursor } = inserirNoTexto(
            alvo.modelo.getValue(),
            ids.map(id => `![imagem](${tokenImagem(id)})`).join('\n'),
            alvo.inicio,
            alvo.fim,
        )
        substituir(alvo, texto, cursor)
    }

    const botoes = [
        {
            titulo: 'Cabeçalho',
            Icone: HeadingIcon,
            acao: () => prefixar(CABECALHO),
        },
        { titulo: 'Negrito', Icone: BoldIcon, acao: () => aplicar('**', '**') },
        { titulo: 'Itálico', Icone: ItalicIcon, acao: () => aplicar('_', '_') },
        {
            titulo: 'Link',
            Icone: LinkIcon,
            acao: () => aplicar('[', '](https://)'),
        },
        { titulo: 'Lista', Icone: ListIcon, acao: () => prefixar(MARCADOR) },
        {
            titulo: 'Lista numerada',
            Icone: ListOrderedIcon,
            acao: () => prefixar(NUMERO),
        },
        {
            titulo: 'Lista de tarefas',
            Icone: ListTodoIcon,
            acao: () => prefixar(TAREFA),
        },
        { titulo: 'Código', Icone: CodeIcon, acao: () => aplicar('`', '`') },
    ]

    // Fase de captura: o Monaco trata drop e paste no próprio textarea interno
    // e inseriria o nome do arquivo antes de a imagem ser anexada.
    const onDragAndDropEvents = {
        onDragOverCapture: (event: React.DragEvent<unknown>) => {
            if (!onAnexarImagens || disabled) return

            event.preventDefault()
            event.stopPropagation()
            setArrastando(true)
        },
        onDragLeaveCapture: (event: React.DragEvent<unknown>) => {
            if (
                Array.isArray(event.currentTarget) &&
                (event.currentTarget as any).contains(
                    event.relatedTarget as Node,
                )
            ) {
                return
            }

            setArrastando(false)
        },
        onDropCapture: (event: React.DragEvent<unknown>) => {
            if (!onAnexarImagens || disabled) return

            event.preventDefault()
            event.stopPropagation()
            setArrastando(false)
            void anexar(Array.from(event.dataTransfer.files))
        },
        onPasteCapture: (event: React.ClipboardEvent<HTMLDivElement>) => {
            const arquivos = Array.from(event.clipboardData.files).filter(
                arquivo => arquivo.type.startsWith('image/'),
            )

            // Só sequestra o paste quando há imagem: texto copiado
            // junto com o arquivo continua caindo no editor.
            if (!arquivos.length || disabled) return

            event.preventDefault()
            event.stopPropagation()
            void anexar(arquivos)
        },
    }

    return (
        <Tabs
            defaultValue="escrever"
            className={cn(
                'rounded-md border relative gap-0 overflow-hidden bg-background! min-h-0',
                arrastando && 'border-primary',
                disabled && 'opacity-disabled pointer-events-none select-none',
                className,
            )}
            aria-disabled={disabled}
        >
            <div className="flex items-center justify-between border-b px-2 pt-1">
                <TabsList className="flex gap-1" variant="underline">
                    <TabsTrigger value="escrever" variant="underline">
                        Escrever
                    </TabsTrigger>
                    <TabsTrigger value="previsualizar" variant="underline">
                        Pré-visualizar
                    </TabsTrigger>
                    {abasExtras.map(aba => (
                        <TabsTrigger
                            key={aba.valor}
                            value={aba.valor}
                            variant="underline"
                        >
                            {aba.rotulo}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <div className="flex items-center [&_svg]:size-5!">
                    {botoes.map(({ titulo, Icone, acao }) => (
                        <Tooltip key={titulo} title={titulo}>
                            <IconButton key={titulo} onClick={acao}>
                                <Icone />
                            </IconButton>
                        </Tooltip>
                    ))}
                    <IconPicker
                        onSelect={nome => aplicar(tokenLucideIcon(nome))}
                    />
                    <Tooltip title="Inserir imagem — também aceita arrastar ou colar">
                        <IconButton
                            disabled={!onAnexarImagens}
                            onClick={() => seletor.current?.click()}
                        >
                            <ImageIcon />
                        </IconButton>
                    </Tooltip>
                    <input
                        ref={seletor}
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={event => {
                            void anexar(Array.from(event.target.files ?? []))
                            event.target.value = ''
                        }}
                    />
                </div>
            </div>

            {/* Sempre montada: desmontar o Monaco perderia o histórico de desfazer. */}
            <TabsContent
                value="escrever"
                forceMount
                className={cn(
                    ALTURA_ABA,
                    'flex flex-col data-[state=inactive]:hidden p-0',
                )}
            >
                <div
                    className={cn('absolute inset-0 pointer-events-auto', {
                        hidden: !disabled,
                    })}
                />
                <div className={ALTURA_ABA} {...onDragAndDropEvents}>
                    <Suspense fallback={CARREGANDO_EDITOR}>
                        <EditorMonaco
                            value={value}
                            textoOriginal={textoOriginal}
                            onChange={valor => onChange(valor ?? '')}
                            onMount={instancia => {
                                editor.current = instancia
                            }}
                            loading={CARREGANDO_EDITOR}
                            options={{
                                placeholder,
                                dropIntoEditor: { enabled: false },
                            }}
                        />
                    </Suspense>
                </div>
                <div
                    className={cn(
                        'absolute inset-0 flex flex-col',
                        'bg-accent opacity-0 gap-2',
                        'pointer-events-none rounded-md',
                        'transition-all duration-200',
                        'items-center justify-center',
                        {
                            'opacity-75': arrastando && !!onAnexarImagens,
                        },
                    )}
                >
                    <ImageUpIcon className="size-12" />
                    <span className="text-xl">Solte para inserir</span>
                </div>
            </TabsContent>

            <TabsContent
                value="previsualizar"
                className={cn(ALTURA_ABA, 'overflow-y-auto py-5 px-7')}
            >
                <MarkdownView resolverImagem={resolverImagem}>
                    {value}
                </MarkdownView>
            </TabsContent>

            {abasExtras.map(aba => (
                <TabsContent
                    key={aba.valor}
                    value={aba.valor}
                    className={cn(ALTURA_ABA, 'overflow-y-auto p-3')}
                >
                    {aba.conteudo}
                </TabsContent>
            ))}
        </Tabs>
    )
}
